import { getWhenOnline, subscribeToNetworkStatus } from "@/domains/network/status";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import type {
  FloatingAgentPendingVoiceRecord,
  FloatingAgentPendingVoiceRepository,
} from "@/domains/dexie-db/repositories";
import { transcribeBlobViaRealtime } from "./floating-agent-blob-transcriber";

type ManagerDeps = {
  repository: FloatingAgentPendingVoiceRepository;
  /** A strictly-false return means delivery was declined and the clip is kept. */
  onTranscriptReady: (transcript: string) => unknown | Promise<unknown>;
  /** A permanently failing clip was dropped without delivering anything. */
  onClipDropped?: () => void;
  transcribe?: (blob: Blob) => Promise<string>;
  whenOnline?: () => Promise<unknown>;
  /** Calls back on network status changes; returns an unsubscribe fn. */
  subscribeToOnline?: (onOnline: () => void) => () => void;
};

/**
 * One transcription attempt per clip per pass; passes are triggered by
 * enqueue, boot restore, and every reconnect, so this many failures means
 * the clip will not recover.
 */
const MAX_LIFETIME_FAILED_ATTEMPTS = 5;

/** decodeAudioData rejections are deterministic — retrying cannot succeed. */
const isPermanentTranscriptionError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "EncodingError";

const defaultSubscribeToOnline = (onOnline: () => void): (() => void) =>
  subscribeToNetworkStatus(status => {
    if (status.isOnline) onOnline();
  });

/**
 * Owns recorded-but-untranscribed push-to-talk clips: persists them (Dexie,
 * restart-safe), then once online replays each clip through the realtime
 * transcriber. Nothing is surfaced in chat until a clip produces non-empty
 * text.
 *
 * Processing is triggered by enqueue, boot-time restore, AND every network
 * reconnect — offline detection lags real connectivity (health-check failure
 * threshold), so an enqueue-time pass can run while actually offline and
 * fail; without the reconnect trigger the clip would strand until restart.
 * A failing clip never blocks the clips behind it, and is dropped once its
 * failures prove it unrecoverable — one undecodable clip retried forever
 * blocked every later clip (MEM-19159).
 */
export class FloatingAgentPendingVoiceManager {
  private readonly repository: FloatingAgentPendingVoiceRepository;
  private readonly onTranscriptReady: (transcript: string) => unknown | Promise<unknown>;
  private readonly onClipDropped: (() => void) | undefined;
  private readonly transcribe: (blob: Blob) => Promise<string>;
  private readonly whenOnline: () => Promise<unknown>;
  private readonly unsubscribeFromOnline: () => void;
  private processing = false;

  constructor({
    repository,
    onTranscriptReady,
    onClipDropped,
    transcribe = transcribeBlobViaRealtime,
    whenOnline = getWhenOnline,
    subscribeToOnline = defaultSubscribeToOnline,
  }: ManagerDeps) {
    this.repository = repository;
    this.onTranscriptReady = onTranscriptReady;
    this.onClipDropped = onClipDropped;
    this.transcribe = transcribe;
    this.whenOnline = whenOnline;
    this.unsubscribeFromOnline = subscribeToOnline(() => {
      void this.processQueue();
    });
  }

  /** Boot-time resume for clips that survived a restart. */
  async restore(): Promise<void> {
    const rows = await this.repository.listOldestFirst();
    if (rows.length > 0) {
      void this.processQueue();
    }
  }

  async enqueue({ blob, mimeType }: { blob: Blob; mimeType: string }): Promise<void> {
    const id = uuidModule.generate();
    logger.info({
      message: "[FloatingAgentPendingVoiceManager] enqueueing clip for deferred transcription",
      info: { pendingId: id, blobSize: blob.size, mimeType },
    });
    await this.repository.add({
      id,
      blob,
      mimeType,
      createdAtIso: new Date().toISOString(),
    });
    void this.processQueue();
  }

  dispose(): void {
    this.unsubscribeFromOnline();
  }

  private async processQueue(): Promise<void> {
    if (this.processing) return;
    this.processing = true;

    try {
      await this.whenOnline();

      const rows = await this.repository.listOldestFirst();
      for (const row of rows) {
        let transcript: string;
        try {
          transcript = await this.transcribe(row.blob);
        } catch (error) {
          await this.recordFailedAttempt(row, error);
          continue;
        }

        // Remove before delivery: a failed delete after delivery would
        // rehydrate the row on the next restore() and send the same
        // transcript twice.
        await this.repository.remove(row.id);
        const trimmedTranscript = transcript.trim();
        if (trimmedTranscript.length === 0) continue;

        const delivered = await this.onTranscriptReady(trimmedTranscript);
        if (delivered === false) {
          // Delivery declined (e.g. the message gate blocked the send).
          // Re-persist the clip and end the pass — later clips would be
          // declined the same way.
          await this.repository.add(row);
          logger.warn({
            message:
              "[FloatingAgentPendingVoiceManager] transcript delivery declined — clip re-queued",
            info: { pendingId: row.id },
          });
          return;
        }
        logger.info({
          message: "[FloatingAgentPendingVoiceManager] delivered deferred transcript",
          info: { pendingId: row.id, blobSize: row.blob.size },
        });
      }
    } finally {
      this.processing = false;
    }
  }

  private async recordFailedAttempt(
    row: FloatingAgentPendingVoiceRecord,
    error: unknown
  ): Promise<void> {
    const failedAttempts = (row.failedAttempts ?? 0) + 1;
    logger.warn({
      message: "[FloatingAgentPendingVoiceManager] deferred transcription attempt failed",
      info: {
        pendingId: row.id,
        failedAttempts,
        blobSize: row.blob.size,
        mimeType: row.mimeType,
        error: String(error),
      },
    });

    const unrecoverable =
      isPermanentTranscriptionError(error) || failedAttempts >= MAX_LIFETIME_FAILED_ATTEMPTS;
    if (!unrecoverable) {
      await this.repository.setFailedAttempts(row.id, failedAttempts);
      return;
    }

    await this.repository.remove(row.id);
    logger.error({
      message:
        "[FloatingAgentPendingVoiceManager] dropping clip after unrecoverable transcription failure",
      info: {
        pendingId: row.id,
        failedAttempts,
        blobSize: row.blob.size,
        mimeType: row.mimeType,
      },
    });
    this.onClipDropped?.();
  }
}
